import InvitationService from '../services/invitationService';
import { notifyInvitesChanged } from './cacheInvalidation';
import { isInviteCurrent } from './comingUpInvites';

// users.avatar can hold an emoji rather than a URL; only a URL is a photo.
export const avatarUrl = (value) =>
  value && (value.startsWith('http') || value.startsWith('/') || value.startsWith('data:')) ? value : null;

/**
 * The cover an invite shows. Both come from the server already resolved for
 * this viewer: a trip's through the viewer-tiered photo lookup (null when the
 * viewer can't open the trip), an activity's through the cover cascade.
 */
export const inviteCover = (invite) =>
  (invite.type === 'trip' ? invite.cover_photo : invite.image_url) || null;

/**
 * Pending invites, shared by Home's Coming up and the planner's Invitations
 * strip so the two can never disagree about what's waiting or how it's
 * answered (docs/prompts/PLANNER_THREE_TABS.md, "Home overlap").
 */

/**
 * The invites still waiting on the viewer: /api/invitations/pending (read from
 * the invitation tables, never notifications) cut to the user's own calendar.
 * The server cuts activities a day loose (UTC) and trips not at all (C291:
 * the bell opens ended trip invitations from the same list); isInviteCurrent
 * is the exact cut for both. Throws on failure, like getPendingInvites, so a caller can keep what's
 * on screen.
 */
export async function fetchCurrentInvites() {
  const invites = await InvitationService.getPendingInvites();
  return invites.filter(inv => isInviteCurrent(inv));
}

/**
 * Answer an invite — for yourself, or for one child you hold full access on
 * (`childId`: a trip's child, or one child named on an activity invitation the
 * lists answer child by child). `response` is 'accepted' | 'maybe' |
 * 'declined'; trips have no maybe.
 *
 * On failure (usually "already answered" — on another device, or in the bell)
 * it fires the invites-changed signal so every list drops the stale invite
 * instead of inviting a retry that can never succeed, then throws.
 * InvitationService fires the same signal on success.
 */
export async function respondToInvite(invite, response, replyMessage = null, childId = null) {
  const result = invite.type === 'trip'
    ? await InvitationService.respondToTripInvitation(invite.id, response, replyMessage, childId)
    : await InvitationService.respondToInvitation(invite.id, response, replyMessage, childId);
  if (!result?.success) {
    notifyInvitesChanged();
    // Callers show their own translated error; this message is only logged.
    throw new Error(result?.error || result?.message);
  }
  return result;
}

/**
 * Can this invite be answered with one tap? Not when it's (also) for a child:
 * a bare "Going" can't say who it's for, so those open the response modal,
 * which asks per person.
 */
export const canAnswerInline = (invite) =>
  invite.self_pending !== false && (invite.child_invitees || []).length === 0;

/**
 * May the viewer's own answer be an accept? Not on an invitation accepting
 * would refuse (C217, C231): the server says so with `self_can_accept` (and
 * `can_accept`), and it may still be declined. Copy never says why. A field
 * the server didn't send reads as acceptable, as before.
 */
export const canAcceptOwn = (invite) =>
  invite.self_can_accept !== false && invite.can_accept !== false;

/**
 * Has this pending invite's day gone by, so it can no longer be answered
 * (C291)? The same exact cut Coming up and the planner make (isInviteCurrent:
 * a trip lasts until its last day; dateless never ends) — they never list an
 * ended invite, and the bell, which does list it, offers it no buttons. A Day
 * Out (`trip_type` 'day_out') never ends: it is a record of a day already
 * lived, and its invitations are sent after that day.
 * @returns {'day'|'trip'|null} 'trip' for a trip over several days, 'day'
 *   for anything on one day, null while it can still be answered
 */
export function inviteEnded(invite, today = undefined) {
  if (!invite) return null;
  if (invite.type === 'trip' && invite.trip_type === 'day_out') return null;
  if (isInviteCurrent(invite, today)) return null;
  const start = (invite.start_date || '').slice(0, 10);
  const end = (invite.end_date || '').slice(0, 10);
  return invite.type === 'trip' && start && end && start !== end ? 'trip' : 'day';
}

/**
 * Notices written before round 22 folded a child into the trip's name:
 * `Sam invited you to join "Lake weekend (for Emma)"`. New ones name the
 * child as the one invited (C292); this reads an old one the same way.
 * Anything else comes back as it is.
 */
const OLD_CHILD_TRIP_INVITE = /^(.*) invited you to join "(.*) \(for ([^()]+)\)"(.*)$/s;
export function readableTripInviteMessage(message) {
  if (typeof message !== 'string') return message;
  const m = message.match(OLD_CHILD_TRIP_INVITE);
  return m ? `${m[1]} invited ${m[3]} to join "${m[2]}"${m[4]}` : message;
}

/**
 * Who a bell trip_invite row asks about, from the buttons the server built
 * for it (server/services/notificationTypes.js): a Respond action carries
 * self / selfCanAccept / children; an accept/decline pair, or a decline alone
 * (withheld, C231), names one child (childId) or none (the viewer). A child's
 * pair is NEVER the viewer's own invitation (C231: opening it offered the
 * parent an Accept). null when the row offers nothing.
 * @returns {{ selfPending: boolean, selfCanAccept: boolean,
 *             childInvitees: { id: string, name: string, canAccept: boolean }[] }|null}
 */
export function tripInviteesFromActions(actions) {
  const list = Array.isArray(actions) ? actions : [];
  const respond = list.find(a => a.type === 'respond_trip_invitation');
  if (respond) {
    return {
      selfPending: respond.self !== false,
      selfCanAccept: respond.selfCanAccept !== false,
      childInvitees: (respond.children || []).map(c => ({ id: c.id, name: c.name, canAccept: c.canAccept !== false }))
    };
  }
  const pair = list.filter(a => a.type === 'accept_trip' || a.type === 'decline_trip');
  if (pair.length === 0) return null;
  const canAccept = pair.some(a => a.type === 'accept_trip');
  const child = pair.find(a => a.childId);
  if (child) {
    return {
      selfPending: false,
      selfCanAccept: false,
      childInvitees: [{ id: child.childId, name: child.childName, canAccept }]
    };
  }
  return { selfPending: true, selfCanAccept: canAccept, childInvitees: [] };
}

/**
 * Shape a pending invite the way InvitationResponseModal reads it. The
 * preview extras — cover, who's going, and a read-only look at the trip — are
 * only here, not in the notification center. The plan link goes to the shared
 * view, not the planner: an invitee isn't a member yet, and the planner would
 * hand them edit controls.
 */
export function toModalInvitation(invite) {
  const isTrip = invite.type === 'trip';
  return {
    id: invite.id,
    title: invite.title,
    location: invite.location,
    ...(isTrip
      ? { startDate: invite.start_date, endDate: invite.end_date }
      : { scheduledDate: invite.scheduled_date, scheduledTime: invite.scheduled_time }),
    fromName: invite.from_user_name,
    fromAvatar: avatarUrl(invite.from_user_avatar) || invite.from_user_avatar,
    customMessage: invite.custom_message,
    // Each child with whether they may be accepted (a withheld one is
    // declined only, C231); the viewer's own answer likewise.
    childInvitees: (invite.child_invitees || []).map(c => ({ ...c, canAccept: c.can_accept !== false })),
    selfPending: invite.self_pending !== false,
    selfCanAccept: canAcceptOwn(invite),
    // A reopened activity invitation: its answer reaches no one (C257).
    reopened: invite.reopened === true,
    coverPhoto: inviteCover(invite),
    going: invite.going || [],
    goingCount: invite.going_count || 0,
    // Only when the viewer can open the trip — a parent answering just for a
    // child often can't (the server says which via can_view_trip).
    planHref: isTrip && invite.can_view_trip ? `/shared/trip/${invite.id}` : null
  };
}
